/**
 * Truck eligibility ("eligible operations").
 *
 * AAM emits a per-truck state event whose `status` code determines which
 * mission modes (trip types) the truck may run. This module is the single
 * client-side source of truth for that mapping and mirrors the SQL function
 * `public.truck_eligibility_modes(text)` — change both together.
 */

export type TripTypeValue = "NVO" | "SVO" | "Manual" | "CI";

/** Status code -> allowed trip types. Unknown codes allow nothing. */
export const ELIGIBILITY_STATUS_MODES: Record<string, TripTypeValue[]> = {
  OFA: ["NVO", "SVO", "Manual"],
  SVO: ["SVO", "Manual"],
  OAR: ["Manual"],
  STOCK: ["Manual"],
};

/** Status code -> human-readable autonomy status label. */
export const AUTONOMY_STATUS_LABELS: Record<string, string> = {
  OFA: "Full Auto",
  OAR: "Auto Restricted",
  SVO: "Supervised Only",
  MANUAL: "Manual Only",
  STOCK: "Manual Only",
};

/** Display label for a status code; falls back to the raw code. */
export function autonomyStatusLabel(status: string | null | undefined): string {
  if (!status) return "—";
  const key = status.trim().toUpperCase();
  return AUTONOMY_STATUS_LABELS[key] ?? status;
}

export interface TruckEligibilityRow {
  tractor_id: string;
  status: string | null;
  allowed_modes: string[] | null;
  eligible_modes: string[] | null;
  reasons: string[] | null;
  constraint_tags: string[] | null;
  decided_by: string | null;
  revision: number | null;
  emitted_at: string | null;
  status_effective_at: string | null;
}

/** Modes allowed by a status code. Unknown/missing status -> empty list. */
export function modesForStatus(status: string | null | undefined): TripTypeValue[] {
  if (!status) return [];
  return ELIGIBILITY_STATUS_MODES[status.trim().toUpperCase()] ?? [];
}

/** Human-readable, comma-delimited mode list for a tractor's latest event. */
export function formatEligibleModes(row: TruckEligibilityRow | undefined | null): string {
  if (!row) return "—";
  const modes = modesForStatus(row.status);
  return modes.length > 0 ? modes.join(", ") : "None";
}

/**
 * Display text for the "Eligible modes" surfaces (Tractors table/card and the
 * detail card), mirroring the order-picker labels so the two never disagree.
 * `showAsBlocked` is presentation only — it marks the states that should render
 * in an error tone. It is NOT the operational gate; that stays in
 * `blockReasonForTripType` / `validateTractorEligibility`.
 */
export function eligibleModesDisplay(
  row: TruckEligibilityRow | undefined | null,
  gateState: EligibilityGateState,
): { text: string; showAsBlocked: boolean } {
  if (gateState === "loading")
    return { text: ELIGIBILITY_LOADING_LABEL, showAsBlocked: false };
  if (gateState === "error")
    return { text: ELIGIBILITY_ERROR_LABEL, showAsBlocked: false };
  if (gateState === "off") return { text: formatEligibleModes(row), showAsBlocked: false };
  if (!row) return { text: ELIGIBILITY_MISSING_LABEL, showAsBlocked: true };
  const modes = modesForStatus(row.status);
  return modes.length > 0
    ? { text: modes.join(", "), showAsBlocked: false }
    : { text: "None", showAsBlocked: true };
}

/**
 * Why a tractor is blocked:
 * - `missing` — no AAM eligibility record on file at all.
 * - `unknown` — a record exists but its status code isn't recognized.
 * - `mode`    — recognized status, but it doesn't allow this trip type.
 */
export type EligibilityBlockReason = "missing" | "unknown" | "mode";

/**
 * Gate state for the whole eligibility feature:
 * - `off`     — feature disabled for this tenant; never block anything.
 * - `loading` — data not fetched yet; block selection/saving *temporarily*.
 * - `error`   — fetch failed; block selection/saving temporarily.
 * - `ready`   — a successful response; missing rows genuinely mean no record.
 */
export type EligibilityGateState = "off" | "loading" | "error" | "ready";

export const ELIGIBILITY_MISSING_LABEL = "Eligibility Data Missing";
export const ELIGIBILITY_LOADING_LABEL = "Checking eligibility…";
export const ELIGIBILITY_ERROR_LABEL = "Eligibility unavailable";

/**
 * Can this tractor run this trip type?
 *
 * Fail closed: no event on file, or an unrecognized status code, blocks every
 * trip type including `CI`. `CI` is exempt from the mode mapping only when a
 * recognized status is on file.
 */
export function isTractorEligibleForTripType(
  row: TruckEligibilityRow | undefined | null,
  tripType: string | null | undefined,
): boolean {
  return blockReasonForTripType(row, tripType) === null;
}

/** Block reason for one tractor + trip type, or `null` when it's allowed. */
export function blockReasonForTripType(
  row: TruckEligibilityRow | undefined | null,
  tripType: string | null | undefined,
): EligibilityBlockReason | null {
  if (!row) return "missing";
  const modes = modesForStatus(row.status);
  if (modes.length === 0) return "unknown";
  if (!tripType) return null;
  if (tripType.trim().toUpperCase() === "CI") return null;
  const wanted = tripType.trim().toLowerCase();
  return modes.some((m) => m.toLowerCase() === wanted) ? null : "mode";
}

/**
 * Per-tractor block reasons for a picker. `tractorIds` must be the ids shown
 * in the picker so trucks absent from the eligibility map are flagged
 * `missing` rather than silently allowed.
 */
export function computeEligibilityReasons(
  byTractor: Map<string, TruckEligibilityRow>,
  tripType: string | null | undefined,
  tractorIds: string[],
  gateState: EligibilityGateState,
): Map<string, EligibilityBlockReason> {
  const reasons = new Map<string, EligibilityBlockReason>();
  if (gateState !== "ready") return reasons;
  for (const id of tractorIds) {
    const reason = blockReasonForTripType(byTractor.get(id), tripType);
    if (reason) reasons.set(id, reason);
  }
  return reasons;
}

/** Tractor ids that must be blocked in a picker for the given trip type. */
export function computeIneligibleTractorIds(
  byTractor: Map<string, TruckEligibilityRow>,
  tripType: string | null | undefined,
  tractorIds: string[],
  gateState: EligibilityGateState = "ready",
): Set<string> {
  return new Set(
    computeEligibilityReasons(byTractor, tripType, tractorIds, gateState).keys(),
  );
}

export interface TractorAssignmentCheck {
  /** Selected tractor id, if any. */
  tractorId: string | null | undefined;
  /** Trip type that applies to that assignment. */
  tripType: string | null | undefined;
  /** Display name for the error message (tractor name). */
  tractorLabel?: string | null;
  /** Optional context, e.g. "Stop 2". */
  context?: string | null;
}

/**
 * Save-time hard block. Returns a user-facing error for the first assignment
 * whose tractor isn't eligible for its trip type, or `null` when all pass.
 * Mirrors the picker gating so a stale selection can't slip through.
 */
export function validateTractorEligibility(
  assignments: TractorAssignmentCheck[],
  byTractor: Map<string, TruckEligibilityRow>,
  gateState: EligibilityGateState = "ready",
): string | null {
  if (gateState === "off") return null;
  const hasAssignment = assignments.some((a) => !!a.tractorId);
  if (gateState !== "ready") {
    if (!hasAssignment) return null;
    return gateState === "loading"
      ? "Truck eligibility is still loading. Try again in a moment."
      : "Truck eligibility data is unavailable right now, so a tractor can't be assigned.";
  }
  for (const a of assignments) {
    if (!a.tractorId) continue;
    const row = byTractor.get(a.tractorId);
    const reason = blockReasonForTripType(row, a.tripType);
    if (!reason) continue;
    const who = a.tractorLabel || "The selected tractor";
    const where = a.context ? ` (${a.context})` : "";
    if (reason === "missing" || reason === "unknown") {
      return `${who}${where}: ${ELIGIBILITY_MISSING_LABEL}.`;
    }
    const modes = formatEligibleModes(row);
    return `${who}${where} is not eligible for ${a.tripType} trips. Eligible modes: ${modes}.`;
  }
  return null;
}

